import { fromJS } from 'immutable';
import _, { compose, partial, pick } from 'underscore';
import { ITextOverlayV2 } from 'blocks/TextOverlayModal/v2';
import { DeepImmutableMap } from 'types';
import { DEFAULT_TRANSITION_DURATION_IN_SEC } from 'utils/constants';
import { scaleInlineStyles } from 'utils/embed/text-overlay';
import measurement, {
  Measurement,
  Pixels,
  ViewportWidth,
} from 'utils/measurement';
import { measurementToPx, placementTransformer } from 'utils/placement';
import { TextOverlay, VideoTemplateStateContent } from '../../types';
import { formatPadding } from './utils';

export function getOnlyTextOverlay(state: VideoTemplateStateContent) {
  const { textOverlays } = state;
  return textOverlays.data[textOverlays.order[0]];
}

/*
 * take a text overlay from state and transform it to the shape expected by
 * the TextOverlayModal
 */
export function getTextModalOverlay(
  state: VideoTemplateStateContent,
  id: string,
): DeepImmutableMap<ITextOverlayV2> {
  const overlay = state.textOverlays?.data?.[id];

  if (!overlay) return undefined;

  const toPx = (m: Measurement) => m.toUnit('px', state.canvas).value;

  return fromJS({
    advancedTextConfigs: overlay.advancedTextConfigs,
    editor: overlay.editor,
    integrationData: overlay.integrationData,
    position: {
      left: toPx(overlay.position.left),
      top: toPx(overlay.position.top),
    },
    size: {
      height: toPx(overlay.size.height),
      width: toPx(overlay.size.width),
    },
    style: {
      ...overlay.style,
      fontSize: toPx(overlay.style.fontSize),
      position: 'absolute',
      // the TextOverlay modal confusingly takes numeric padding values which are
      // assumed to be in vw units.  these values are saved in VW in state, so
      // we just take the value and pass it to the TextOverlay template as is
      ...formatPadding(overlay.style, (val: Measurement) => val.value),
    },
    text: overlay.text,
    textBuilderStyles: overlay.textBuilderStyles,
    textHtml: scaleInlineStyles(
      overlay.textHtml,
      ['fontSize'],
      val => `${toPx(new ViewportWidth(val))}px`,
    ),
    viewport: { ...state.canvas },
    version: overlay.version,
  });
}

export function exportTextOverlay(
  overlay: TextOverlay,
  state: VideoTemplateStateContent,
): ITextOverlayV2 & { layerId: number } {
  const { canvas, layers } = state;

  // TODO types
  const convertToNumericPx = compose(
    partial(placementTransformer, _, (v: Pixels) => v.value),
    partial(measurementToPx, _, canvas),
  );

  return {
    ...overlay,
    advancedTextConfigs: overlay.advancedTextConfigs,
    editor: overlay.editor,
    layerId: layers.order.indexOf(overlay.layerId),
    position: convertToNumericPx(pick(overlay.position, 'top', 'left')),
    size: convertToNumericPx(pick(overlay.size, 'height', 'width')),
    style: {
      ...formatPadding(
        overlay.style,
        (val: Measurement) => val.toUnit('vw', canvas).value,
      ),
    },
    textBuilderStyles: overlay.textBuilderStyles,
    // TODO scaleInlineStyles strips unit for legacy reasons but it would
    // be useful here to know that it's actually in vw without making an assumption
    textHtml: scaleInlineStyles(overlay.textHtml, ['fontSize'], size =>
      measurement(size, 'vw')
        .toUnit('px', canvas)
        .toString(),
    ),
    time: {
      endMillis: 5000,
      startMillis: 0,
    },
    version: overlay.version,
    viewport: canvas,
    transitions: {
      in: {
        duration: DEFAULT_TRANSITION_DURATION_IN_SEC,
        value: 'cut',
      } as any,
      out: {
        duration: DEFAULT_TRANSITION_DURATION_IN_SEC,
        value: 'cut',
      } as any,
    },
  };
}

export function exportTextOverlays(
  state: VideoTemplateStateContent,
): Array<ITextOverlayV2 & { layerId: number }> {
  const { textOverlays } = state;

  if (!textOverlays?.order || textOverlays.order.length === 0) {
    return undefined;
  }

  return textOverlays.order.map(id =>
    exportTextOverlay(textOverlays.data[id], state),
  );
}
